import { useState, useCallback, useEffect } from "react";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import Addicon from "@mui/icons-material/Add";
import dateformat from "dateformat";
import { useSnackbar } from "react-simple-snackbar";

import { Button, Modal, Text } from "$components";
import { useStyles } from "./TreatmentTab.styles";

import {
  checkIfShippingRecordIsCreatable,
  createDeliveryAddressFromOp,
  createShippingAddressFromOp,
  EDB_DISPOSITION_DATE_TYPE_LABELS,
  getDispositionDateTypeLabel,
  getPreparationOrderStatusLabelByRole,
  getShippingRecordCreationErrorMessage,
  ShippingRecordCreationError,
} from "$utils/typeConstants";
import { createShippingRecord } from "$services/ShippingRecord.service";
import useJStyles from "$hooks/useJStyles";
import useAuth from "$hooks/useAuth";
import { snackbarErrorOptions } from "$utils/SnackbarOptions";
import JTable from "$components/JTable/JTable";
import GenericSelect from "$forms/GenericSelect";
import CompanyFilterInput from "$forms/CompanyFilterInput";
import {
  DispositionType,
  PreparationOrder,
  PreparationOrderStatus,
} from "$types";
import { CellExpander } from "./CellExpander";
import { DEFAULT_DATE_FORMAT, toLocalDateString } from "$utils/utils";
import ParcelTable from "$containers/Parcel/ParcelTable";
import BataillogDatePicker from "$components/BataillogDatePicker/BataillogDatePicker";
import { MISSING_SHIPPING_ADDRESS, MISSING_SHIPPING_REF } from "../constants";

interface TypeRow {
  row: { original: PreparationOrder & { disabled: boolean } };
}

export const FilterForm = ({
  ops,
  isLoading,
  setSelectedOpToAddInDrawer,
  togglePopin,
  page,
  setPage,
  total,
  filters,
  setFilters,
  companySidebarFilter,
  rowsPerPage,
  setRowsPerPage,
  handleUrlParams,
  urlParams,
  selectedOPs,
  setSelectedOPs,
}: any) => {
  const [recordCreationError, setRecordCreationError] = useState<string>("");
  const [openErrorSnackbar] = useSnackbar(snackbarErrorOptions);
  const classes = useStyles();
  const { jclasses } = useJStyles();
  const { isAdmin } = useAuth();
  const { userRole } = useAuth();
  const [dateStart_tfilter, setDateStart_tfilter] = useState<
    Date | undefined | null
  >(null);
  const [dateEnd_tfilter, setDateEnd_tfilter] = useState<
    Date | undefined | null
  >(null);
  const [tmpPage, setTmpPage] = useState(10000);

  const handleOpenDrawer = useCallback(
    (op: PreparationOrder) => {
      setSelectedOpToAddInDrawer(op);
      togglePopin();
    },
    [setSelectedOpToAddInDrawer, togglePopin]
  );

  const renderRowSubComponent = useCallback(
    ({ row }) => {
      const op: PreparationOrder = row.original;
      return (
        <div className={classes.subRow}>
          <div className={classes.opParcelTitle}>Colisage</div>
          <div className={classes.parcelTable}>
            <ParcelTable parcel={op.parcel} showDetailsTable />
          </div>
        </div>
      );
    },
    [classes]
  );

  const handleShippingRecordCreation = useCallback(async () => {
    if (selectedOPs.length === 0) {
      setRecordCreationError(
        getShippingRecordCreationErrorMessage(
          ShippingRecordCreationError.NO_OP_ERROR
        )
      );
      return;
    }

    if (selectedOPs.some((op) => !op.refPreparationEntrepot)) {
      setRecordCreationError(MISSING_SHIPPING_REF);
      return;
    }

    if (selectedOPs.some((op) => !op.shipAdress)) {
      setRecordCreationError(MISSING_SHIPPING_ADDRESS);
      return;
    }

    const { deliveryDate, error } =
      checkIfShippingRecordIsCreatable(selectedOPs);
    if (error) {
      setRecordCreationError(error);
      return;
    }

    const missingNameInToAddress = selectedOPs.some((op) => {
      const toAddress = createDeliveryAddressFromOp(op);
      return !toAddress || !toAddress.name;
    });

    if (missingNameInToAddress) {
      setRecordCreationError(MISSING_SHIPPING_ADDRESS);
      return;
    }

    try {
      await createShippingRecord({
        wishedDeliveryDate: toLocalDateString(deliveryDate),
        shipments: selectedOPs.map((op) => ({
          opId: op._id,
          companyId: op.company?._id ?? op.companyId ?? null,
          refPreparation: op.refPreparationEntrepot,
          parcelId: op.parcel.id,
          fromAddress: createShippingAddressFromOp(op),
          toAddress: createDeliveryAddressFromOp(op),
        })),
      });
      setSelectedOPs([]);
    } catch (error: any) {
      openErrorSnackbar(
        error.message || "Erreur lors de la création du dossier"
      );
    }
  }, [selectedOPs, openErrorSnackbar, setSelectedOPs]);

  // Clear company filter when sidebar filter is first activated
  useEffect(() => {
    if (companySidebarFilter?._id && filters.company) {
      setFilters((prev) => ({ ...prev, company: null }));
      handleUrlParams("company", null);
    }
  }, [companySidebarFilter?._id, filters.company, handleUrlParams, setFilters]);

  const columns = [
    {
      Header: "Entreprise",
      filterName: "company",
      filterComponent: (props: any) => {
        return (
          <CompanyFilterInput
            value={companySidebarFilter?.name || props.value || ""}
            label=""
            placeholder={companySidebarFilter?.name || "Par nom"}
            onChange={(companyName) => {
              if (companyName && companyName.trim()) {
                setFilters({ ...filters, company: companyName });
                handleUrlParams("company", companyName);
              } else {
                setFilters({ ...filters, company: null });
                handleUrlParams("company", null);
              }
            }}
            disabled={!!companySidebarFilter?.name}
          />
        );
      },
      maxWidth: 120,
      minWidth: 120,
      Cell: ({ row: { original: currentOP } }: any) => {
        const orderCompanyName = currentOP?.company?.name || "N/A";
        return (
          <Text
            className={currentOP.disabled ? classes.disabledCell : classes.cell}
            type={currentOP?.company?.isActive ? "important" : "disabled"}
            style={{
              whiteSpace: "nowrap",
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            onClick={() => {
              // setSelectedOp(company);
              // setPopinOpen(true);
            }}
          >
            {orderCompanyName}
          </Text>
        );
      },
    },
    {
      Header: "N° OP",
      filterName: "code",
      filterComponent: (props: any) => {
        return (
          <TextField
            placeholder="Par n° OP"
            variant="outlined"
            type="text"
            value={urlParams.get("code")}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setFilters((prevFilters) => ({
                ...prevFilters,
                code: e.target.value,
              }));
              handleUrlParams("code", e.target.value);
            }}
          />
        );
      },
      maxWidth: 100,
      minWidth: 100,
      Cell: ({ row: { original: currentOP } }: any) => {
        return (
          <Text
            type="important"
            className={currentOP.disabled ? classes.disabledCell : classes.cell}
          >
            {currentOP?.code}
          </Text>
        );
      },
    },
    {
      Header: "Ref prépa entrepôt",
      filterName: "refPreparationWarehouse",
      filterComponent: (props: any) => {
        return (
          <TextField
            placeholder="Par ref prépa entrepôt"
            value={urlParams.get("refPreparationWarehouse")}
            variant="outlined"
            type="text"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setFilters((prevFilters) => ({
                ...prevFilters,
                refPreparationWarehouse: e.target.value,
              }));
              handleUrlParams("refPreparationWarehouse", e.target.value);
            }}
            style={{ width: "100%" }}
          />
        );
      },
      maxWidth: 100,
      minWidth: 100,
      Cell: ({ row: { original: currentOP } }: any) => {
        return (
          <Text
            type="important"
            className={currentOP.disabled ? classes.disabledCell : classes.cell}
          >
            {currentOP?.refPreparationEntrepot}
          </Text>
        );
      },
    },
    {
      Header: "Statut OP",
      filterName: "status",
      minWidth: 100,
      maxWidth: 100,
      filterComponent: (props: any) => {
        return (
          <GenericSelect
            className={jclasses.jTableLabelHeader}
            onChange={(val) => {
              props.onChange(val);
              setFilters((prevFilters) => ({
                ...prevFilters,
                status: val,
              }));
              handleUrlParams("status", val);
            }}
            value={
              urlParams.get("status")
                ? [parseInt(urlParams.get("status"), 10)]
                : [props.value]
            }
            options={[
              { value: PreparationOrderStatus.PREPARED, label: "Préparé" },
              { value: PreparationOrderStatus.IN_PROGRESS, label: "En cours" },
            ]}
            multiple
          />
        );
      },
      Cell: ({ row: { original: currentOP } }: any) => {
        return (
          <div
            className={currentOP.disabled ? classes.disabledCell : classes.cell}
          >
            {getPreparationOrderStatusLabelByRole(userRole, currentOP?.status)}
          </div>
        );
      },
    },
    {
      Header: "Lieu enlèvement",
      filterName: "warehouseName",
      filterComponent: (props: any) => {
        return (
          <TextField
            placeholder="Par lieu enlèvement"
            value={props.value}
            variant="outlined"
            type="text"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setFilters((prevFilters) => ({
                ...prevFilters,
                warehouseName: e.target.value,
              }));
              handleUrlParams("warehouseName", e.target.value);
            }}
            style={{ width: "100%" }}
          />
        );
      },
      maxWidth: 160,
      minWidth: 160,
      Cell: ({ row: { original } }: any) => {
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div>
              <Text
                style={{ fontWeight: 700 }}
                className={
                  original?.disabled ? classes.disabledCell : classes.cell
                }
                type={original?.disabled ? "text" : "important"}
              >
                {original?.entrepot?.name}
              </Text>
            </div>
            <div>{`${original?.entrepot?.location?.cp} ${original?.entrepot?.location?.city}`}</div>
          </div>
        );
      },
    },
    {
      Header: "Lieu livraison",
      filterName: "dockyardCode",
      filterComponent: (props: any) => {
        return (
          <TextField
            placeholder="Par lieu livraison"
            value={props.value}
            variant="outlined"
            type="text"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setFilters((prevFilters) => ({
                ...prevFilters,
                dockyardCode: e.target.value,
              }));
              handleUrlParams("dockyardCode", e.target.value);
            }}
            style={{ width: "100%" }}
          />
        );
      },
      maxWidth: 160,
      minWidth: 160,
      Cell: ({ row: { original: currentOP } }: TypeRow) =>
        currentOP.shipType === DispositionType.ON_DOCKYARD ? (
          <div
            className={currentOP.disabled ? classes.disabledCell : classes.cell}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div>
                <Text
                  className={
                    currentOP.disabled ? classes.disabledCell : classes.cell
                  }
                  type={currentOP.disabled ? "text" : "important"}
                  style={{ fontWeight: 700 }}
                >
                  {currentOP.dockyard.name ?? ""}
                  {currentOP.chantier.name ?? ""}
                </Text>
                {` ${currentOP.chantier.name ?? ""}${
                  currentOP.dockyard.name ?? ""
                }`}
              </div>
              <div>{`${currentOP.shipPostalCode ?? ""} ${
                currentOP.shipCity ?? ""
              }`}</div>
            </div>
          </div>
        ) : (
          <div
            className={currentOP.disabled ? classes.disabledCell : classes.cell}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div>
                <Text
                  className={
                    currentOP.disabled ? classes.disabledCell : classes.cell
                  }
                  type={currentOP.disabled ? "text" : "important"}
                  style={{ fontWeight: 700 }}
                >
                  {currentOP.shipEntrepotName || currentOP.shipPlace}
                </Text>
              </div>
              <div>{`${currentOP.shipAdress} ${currentOP.shipPostalCode} ${currentOP.shipCity}`}</div>
            </div>
          </div>
        ),
    },
    {
      Header: "Type de date",
      filterName: "dispositionDateType",
      filterComponent: (props: any) => {
        return (
          <GenericSelect
            className={jclasses.jTableLabelHeader}
            onChange={(val) => {
              props.onChange(val);
              setFilters((prevFilters) => ({
                ...prevFilters,
                dispositionDateType: val,
              }));
              handleUrlParams("dispositionDateType", val);
            }}
            value={
              urlParams.get("dispositionDateType")
                ? urlParams.get("dispositionDateType")
                : props.value
            }
            options={[
              {
                value: "LATEST",
                label: EDB_DISPOSITION_DATE_TYPE_LABELS[0],
              },
              { value: "EXACT", label: EDB_DISPOSITION_DATE_TYPE_LABELS[1] },
            ]}
          />
        );
      },
      minWidth: 100,
      maxWidth: 100,
      Cell: ({ row: { original: currentOP } }: TypeRow) => (
        <Text
          className={currentOP.disabled ? classes.disabledCell : classes.cell}
        >
          {currentOP.edb ? getDispositionDateTypeLabel(currentOP?.edb) : null}
        </Text>
      ),
    },
    {
      Header: "Date de livraison souhaitée",
      filterName: "shippingDate",
      filterComponent: () => (
        <BataillogDatePicker
          selectsRange
          startDate={dateStart_tfilter}
          endDate={dateEnd_tfilter}
          onDateChange={(dates: Date | [Date, Date]) => {
            if (Array.isArray(dates)) {
              const [start, end] = dates;
              setDateStart_tfilter(start);
              setDateEnd_tfilter(end);
              if (!start && !end) {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  shippingStartDate: start,
                }));
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  shippingEndDate: end,
                }));
                setDateStart_tfilter(null);
                setDateEnd_tfilter(null);
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  shippingEndDate: end,
                }));
              } else if (start === end || !end) {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  shippingStart: dateformat(start, "yyyy-mm-dd"),
                }));
              } else {
                // setFilters("shippingDate", undefined); // This line seems suspicious/wrong for a state setter, commenting out or ignoring for now as I am fixing date format.
                // Actually, let's just fix the date format in the updaters.
                setFilters((prevFilters) => {
                  const newFilters = { ...prevFilters };
                  delete newFilters.shippingDate;
                  return {
                    ...newFilters,
                    shippingStartDate: dateformat(start, "yyyy-mm-dd"),
                    shippingEndDate: dateformat(end, "yyyy-mm-dd"),
                  };
                });
              }
            }
          }}
        />
      ),
      minWidth: 90,
      maxWidth: 90,
      Cell: ({ row: { original: currentOP } }: TypeRow) => (
        <Text
          className={currentOP.disabled ? classes.disabledCell : classes.cell}
        >
          {currentOP?.edb?.dispositionDate
            ? dateformat(currentOP?.edb?.dispositionDate, DEFAULT_DATE_FORMAT)
            : "N/A"}
        </Text>
      ),
    },
    {
      Header: null,
      accessor: "expander",
      minWidth: 60,
      maxWidth: 60,
      preventClickAction: true,
      Cell: ({ row }: any) => (
        <CellExpander
          row={row}
          handleOpenDrawer={handleOpenDrawer}
          disabled={row?.original?.disabled}
        />
      ),
    },
  ];

  const handleSwitch = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      onlyWithParcel: !prevFilters.onlyWithParcel,
    }));
    handleUrlParams("onlyWithParcel", !filters.onlyWithParcel);
  };

  // fixme: this is a workaround to fix the issue with the pagination
  const handleChangeRowsPerPage = (rows: number) => {
    setRowsPerPage(rows);
    if (rows > 10) {
      setTmpPage(0);
    } else {
      setTmpPage(10000);
    }
    setPage(0);
  };

  // fixme: this is a workaround to fix the issue with the pagination
  const handleChangePageIndex = (index: number) => {
    if (tmpPage !== page) {
      setPage(index);
      setTmpPage(10000);
    }
  };

  const pageCount = Math.ceil(total / rowsPerPage);

  return (
    <>
      <Box display="flex" alignItems="flex-start">
        <FormControlLabel
          control={
            <Switch
              onChange={handleSwitch}
              checked={filters.onlyWithParcel}
              color="primary"
            />
          }
          label="Afficher les OP avec colisage"
        />

        <Box className={classes.addRecordButtonContainer}>
          <Button
            onClick={handleShippingRecordCreation}
            className={classes.addRecordButton}
            variant="contained"
            disabled={
              isLoading ||
              selectedOPs.length === 0 ||
              selectedOPs.some((op) => !op.parcel)
            }
            startIcon={<Addicon />}
          >
            {isLoading ? <CircularProgress /> : "Créer un dossier"}
          </Button>
        </Box>
      </Box>

      <Box>
        <Text>OP sélectionné(s) : {selectedOPs.length}</Text>
      </Box>

      <Modal
        title="Impossible de créer le dossier"
        textValidate="OK"
        open={recordCreationError !== ""}
        onValidate={() => setRecordCreationError("")}
        onCancel={() => setRecordCreationError("")}
        noCancelButton
      >
        <Box className={jclasses.dialogContent} style={{ textAlign: "center" }}>
          <Text>
            {recordCreationError}
            <br />
            Veuillez revoir la sélection afin de créer le dossier
          </Text>
        </Box>
      </Modal>

      <JTable
        hideCheckboxes={!isAdmin()}
        data={ops ?? []}
        columns={columns}
        isFetching={isLoading}
        filters={filters}
        // setFilter={setFilters}
        setPageIndex={handleChangePageIndex}
        pageIndex={page}
        pageCount={pageCount}
        renderRowSubComponent={renderRowSubComponent}
        // setSelectedItem={setSelectedOPs}
        setSelectedRows={setSelectedOPs}
        selectedRows={selectedOPs}
        pageName="opList"
        noDataText="Aucune op n'a encore été créée"
        rowsPerPage={rowsPerPage}
        setRowsPerPage={handleChangeRowsPerPage}
      />
    </>
  );
};
